"use client";

import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { linePath, type Point } from "@/lib/tool-kit/charts/lines";
import { extent, linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const WIDTH = 720;
const HEIGHT = 300;
const MARGIN = { left: 64, right: 18, top: 16, bottom: 52 };

export interface PlotSeries {
  /** x positions (bases). */
  xs: readonly number[];
  /** y values; null leaves a gap. */
  ys: readonly (number | null)[];
}

/**
 * A line plot of a sliding-window profile, drawn as an SVG that carries its own colours so
 * the exported file looks the same as the preview.
 */
export function WindowPlot({
  id,
  series,
  reference,
  xTitle,
  yTitle,
  label,
  description,
  format,
  yRange,
}: {
  id: string;
  series: PlotSeries;
  /** A horizontal reference line, such as the whole-sequence mean. */
  reference: number | null;
  xTitle: string;
  yTitle: string;
  label: string;
  description: string;
  format: (value: number) => string;
  /** Fixed y domain; otherwise fitted to the data. */
  yRange?: readonly [number, number];
}) {
  const plot = {
    x: MARGIN.left,
    y: MARGIN.top,
    width: WIDTH - MARGIN.left - MARGIN.right,
    height: HEIGHT - MARGIN.top - MARGIN.bottom,
  };
  const values = series.ys.filter((v): v is number => v !== null);
  const fitted = extent(
    reference === null ? values : [...values, reference]
  ) ?? [0, 1];
  const [low, high] = yRange ?? fitted;
  const ticksY = niceTicks(low, high === low ? low + 1 : high, { count: 5 });
  const [firstX, lastX] = extent(series.xs) ?? [1, 1];
  const ticksX = niceTicks(firstX, lastX === firstX ? firstX + 1 : lastX, {
    count: 6,
    exact: true,
  });
  const yScale = linearScale(
    [ticksY.min, ticksY.max],
    [plot.y + plot.height, plot.y]
  );
  const xScale = linearScale(
    [firstX, lastX],
    [plot.x + 8, plot.x + plot.width - 8]
  );
  const points: (Point | null)[] = series.xs.map((x, i) => {
    const y = series.ys[i];
    return y === null || y === undefined ? null : [xScale(x), yScale(y)];
  });
  const single = points.length === 1 ? points[0] : null;
  return (
    <ChartSvg
      description={description}
      height={HEIGHT}
      id={id}
      label={label}
      width={WIDTH}
    >
      <ValueAxis
        format={format}
        orient="left"
        plot={plot}
        scale={yScale}
        ticks={ticksY.ticks}
      />
      <ValueAxis
        format={(value) => String(Math.round(value))}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={xScale}
        ticks={ticksX.ticks}
      />
      {reference === null ? null : (
        <line
          stroke={CHART_COLORS[3]}
          strokeDasharray="5 4"
          strokeWidth={1.5}
          x1={px(plot.x)}
          x2={px(plot.x + plot.width)}
          y1={px(yScale(reference))}
          y2={px(yScale(reference))}
        />
      )}
      <path
        d={linePath(points)}
        fill="none"
        stroke={CHART_COLORS[0]}
        strokeLinejoin="round"
        strokeWidth={1.75}
      />
      {single ? (
        <circle
          cx={px(single[0])}
          cy={px(single[1])}
          fill={CHART_COLORS[0]}
          r={3.5}
        />
      ) : null}
      <text
        fill={CHART_INK.muted}
        fontSize={12}
        textAnchor="middle"
        x={px(plot.x + plot.width / 2)}
        y={HEIGHT - 10}
      >
        {xTitle}
      </text>
      <text
        fill={CHART_INK.muted}
        fontSize={12}
        textAnchor="middle"
        transform={`rotate(-90 14 ${px(plot.y + plot.height / 2)})`}
        x={14}
        y={px(plot.y + plot.height / 2)}
      >
        {yTitle}
      </text>
    </ChartSvg>
  );
}
